import React, { useEffect, useState } from "react";
import { Form, Row, Col, Container } from "react-bootstrap";
import PropTypes from 'prop-types';
import { object, string, mixed, array as yuparray } from 'yup';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { FormControl, FormLabel, makeStyles, Button, Card, CardContent, SvgIcon } from '@material-ui/core';
import BackNext from '../../Common/SubComponents/BackNext';
import { CheckboxInput, TextInput, SelectInput, StyledRadioInput } from "../../../Helpers/Inputs";
import InsuranceProviders from '../../../Helpers/Lists/InsuranceProviders';
import { SESSION_STORAGE_ACCOUNT_KEY, getSessionStorageItem } from '../../../../sessionStorage';
import { CALIFORNIA, FLORIDA, KANSAS, MASSACHUSETTS, OKLAHOMA, WYOMING, MICHIGAN, NEVADA, PENNSYLVANIA, WASHINGTON, DELAWARE, NORTH_DAKOTA, SOUTH_DAKOTA } from '../../../Helpers/Constants/States';
import { NumberFormatDollars, InputMaskDate } from '../../../Helpers/Formatters';
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.js';
import Step7Icon from '../icons/step_7.svg';
import { isBefore, isAfter, isValid } from 'date-fns';
import ExceedCovgAmountModal from "../Helpers/ExceedCovgAmountModal";

const stateList = ["AK", "AL", "AR", "AZ", "CO", "CT", "DE", "GA", "HI", "IA", "ID", "IL", "IN", "KY", "LA", "MD", "ME", "MN", "MO", "MS", "MT", "NC", "NE", "NH", "NJ", "NM", "OH", "OR", "RI", "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WI", "WV"];
const excludeReplacingReasonStates = ["CA", "DC", "FL", "KS", "MA", "MI", "ND", "NV", "OK", "PA", "WA", "WY"];

const useStyles = makeStyles((theme) => ({
  labelFont: {
    '&.MuiFormLabel-root': {
      color: '#212529',
      lineHeight: '1.5',
    },
  },
  multiLine: {
    width: 'max-content',
  },
  setCheckBox: {
    alignItems: 'start',
  },
  setMargin: {
    marginTop: '-7px',
  }
}));

export default function PolicyPurposeConfirmation(props) {

  const [isLoading, setIsLoading] = useState(false);
  const [showFailureMessage, setShowFailureMessage] = useState(false);
  const [showValidationMessage, setShowValidationMessage] = useState(false);
  const [exceedCovgAmtModalShow, setExceedCovgAmtModalShow] = useState(false);

  const { register, errors, handleSubmit, setValue, triggerValidation, reset } = useForm({
    validationSchema: PolicyPurposeConfirmation.ValidationSchema(props.fullFormState.steps.basicInfo.birthdate),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const classes = useStyles();
  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;
  const state = props.fullFormState.steps.basicInfo && props.fullFormState.steps.basicInfo.state;
  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
  };

  useEffect(() => {

    props.formState.policies.forEach((policy, index) => {
      register({ name: `policies[${index}].insuredName` });
      setValue(`policies[${index}].insuredName`, policy.insuredName);

      register({ name: `policies[${index}].company` });
      setValue(`policies[${index}].company`, policy.company);

      register({ name: `policies[${index}].ownerName` });
      setValue(`policies[${index}].ownerName`, policy.ownerName);

      register({ name: `policies[${index}].insuranceDate` });
      setValue(`policies[${index}].insuranceDate`, policy.insuranceDate);

      register({ name: `policies[${index}].faceAmount` });
      setValue(`policies[${index}].faceAmount`, policy.faceAmount);

      register({ name: `policies[${index}].policyNumber` });
      setValue(`policies[${index}].policyNumber`, policy.policyNumber);

      register({ name: `policies[${index}].insurerName` });
      setValue(`policies[${index}].insurerName`, policy.insurerName);

    });


    register({ name: 'pendingApplications' });
    setValue('pendingApplications', props.formState.pendingApplications);

    register({ name: 'existingCoverage' });
    setValue('existingCoverage', props.formState.existingCoverage);

    register({ name: 'replaceExistingCoverage' });
    setValue('replaceExistingCoverage', props.formState.replaceExistingCoverage);

    register({ name: 'receiveComparativeInfoForm' });
    setValue('receiveComparativeInfoForm', props.formState.receiveComparativeInfoForm);

    register({ name: 'terminateExistingPolicy' });
    setValue('terminateExistingPolicy', props.formState.terminateExistingPolicy);

    register({ name: 'useExistingPolicyFunds' });
    setValue('useExistingPolicyFunds', props.formState.useExistingPolicyFunds);



    register({ name: 'state' });
    setValue('state', state);
  }, [register]);

  const onSubmitError = () => {
    var body = {
      'consumerAccountId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).account.accountId,
      'oneClickAccountId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).account.oneClickAccountId,
      'dob': props.fullFormState.steps.basicInfo.birthdate,
      'firstname': props.fullFormState.steps.personalInfo.firstName,
      'lastname': props.fullFormState.steps.personalInfo.lastName,
      'gender': props.fullFormState.steps.basicInfo.gender,
      'healthclass': props.fullFormState.steps.quoter.healthClass,
      'state': props.fullFormState.steps.gettingToKnowYou.mailingState,
      'zipCode': props.fullFormState.steps.gettingToKnowYou.mailingZip,
      'phone': props.fullFormState.steps.identityVerification.secondaryAddresseePhoneNumber,
      'face': props.fullFormState.steps.quoter.coverageAmount,
      'tobacco': props.fullFormState.steps.basicInfo.tobacco === 'true',
      'email': props.fullFormState.steps.basicInfo.email || props.fullFormState.steps.personalInfo.emailAddress,
      'term': parseInt(props.fullFormState.steps.quoter.term, 10),
      'quotedpremium': props.fullFormState.steps.quoter.premium,
      'quotedpremiummode': 'M',
      'specificappointmentutc': null,
      'specialNote': 'Concierge case created due to Americo Loan Protector Underwriting API Failure, please reach out to consumer for assistance.',
    }
    fetch(`/api/Case/PostApp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
      },
      body: JSON.stringify(body),
    }).then(response => response.json())
      .then((data) => {
        if (data.success) {
          setShowFailureMessage(true);
        }
        setIsLoading(false);
      }).catch((error) => {
        setIsLoading(false);
      });
  }

  const onSubmit = () => {
    if (props.fullFormState.steps.quoter.coverageAmount > 1000000)
      setExceedCovgAmtModalShow(true);
    else {
    let totalDeathBenefit = 0;
    props.formState.policies.forEach((policy, index) => {
      totalDeathBenefit += parseInt(policy.faceAmount, 10);
    });

    if (props.formState.existingCoverage === "true" && props.formState.replaceExistingCoverage === "true" && totalDeathBenefit > props.formState.totalInsuredAmountExternal) {
      setShowValidationMessage(true);
    }
    else {
      setShowValidationMessage(false);
      setShowFailureMessage(false);
      setIsLoading(true);
      // fix tobacco to bool even though we asked for timeline...
      props.fullFormState.steps.basicInfo.tobacco = props.fullFormState.steps.basicInfo.tobacco !== 'Never';
      fetch(`api/Americo/Submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
        },
        body: JSON.stringify(props.fullFormState),
      }).then(res => res.json())
        .then(data => {
          if (data.policyNumber !== "" && data.policyNumber !== "error") {
            props.setPolicyNumber(data.policyNumber);
          } else {
            props.onDeclined();
          }
        }).catch((error) => {
          onSubmitError();
        });
      }
    }
  };

  //go next only after policynumber has been set to form state to ensure it's logged
  useEffect(() => {
    if (props.fullFormState.policyNumber !== "" && props.fullFormState.policyNumber !== "error") {
      props.onNext();
    }
  }, [props.fullFormState.policyNumber]);

  useEffect(() => {
    if (props.formState.existingCoverage === "false") {
      props.onFieldChange('replaceExistingCoverage', "false");
    }
  }, [props.formState.existingCoverage]);

  const getStateSpecificVerbiage = () => {
    switch (state) {
      case CALIFORNIA: return (
        <div>
          <p className="agreementCardTextBold">Are you thinking about buying a new life insurance policy or annuity and discontinuing or changing an existing one? If you are, your decision could be a good one – or a mistake. You will not know for sure unless you make a careful comparison of your existing benefits and the proposed benefits.</p>
          <p className="agreementCardTextBold">Make sure you understand the facts. You should ask the company or agent that sold you your existing policy to give you information about it. </p>
          <p className="agreementCardTextBold">Hear both sides before you decide. This way you can be sure you are making a decision that is in your best interest.</p>
          <p className="agreementCardTextBold">We are required by law to notify your existing company that you may be replacing their policy.</p>
        </div>
      );
      case FLORIDA: return (
        <div>
          <p className="agreementCardTextBold">A decision to buy a new policy and discontinue or change an existing policy may be a wise choice or a mistake.</p>
          <p className="agreementCardTextBold">Get all the facts. Make sure you fully understand both the proposed policy and your existing policy or policies. New policies may contain clauses which limit or exclude coverage of certain events in the initial period of the contract, such as the suicide and incontestable clauses which may have already been satisfied in your existing policy or policies.</p>
          <p className="agreementCardTextBold">Your best source for facts on the proposed policy is the proposed company and its agent. The best source on your existing policy is the existing company and its agent.</p>
          <p className="agreementCardTextBold">Hear from both before you make your decision. This way you can be sure your decision is in your best interest.</p>
          <p className="agreementCardTextBold">Florida regulations give you the right to receive a written Comparative Information Form which summarizes your policy values.</p>
          <p className="agreementCardTextBold">DO NOT TAKE ACTION TO TERMINATE YOUR EXISTING POLICY UNTIL YOUR NEW POLICY HAS BEEN ISSUED AND YOU HAVE EXAMINED IT AND FOUND IT ACCEPTABLE.</p>
        </div>
      );
      case KANSAS: return (
        <div>
          <p className="agreementCardTextBold">You have indicated that you intend to replace an existing life insurance policy or policies in connection with the purchase of our life insurance policy. So please read below points carefully.</p>
          <p className="agreementCardTextBold">Whether it is to your advantage to replace your existing insurance coverage, only you can decide. It is in your best interest, however, to have adequate information before a decision to replace your present coverage becomes final so that you may understand the essential features of the proposed policy and your existing insurance coverage.</p>
          <p className="agreementCardTextBold">You may want to contact your existing life insurance company or its agent for additional information and advice or discuss your purchase with other advisors. The information you receive should be of value to you in reaching a final decision. If either the proposed policy or the existing insurance you intend to replace is a participating policy, you should be aware that dividends may materially reduce the cost of insurance and are an important factor to consider. Dividends, however, are not guaranteed.</p>
          <p className="agreementCardTextBold">You should recognize that a policy which has been in existence for a period of time may have certain advantages to you over a new policy. If the policy coverages are basically similar, the premiums for a new policy may be higher because rates increase as your age increases. Under your existing policy, the period of time during which the issuing company could (contest the policy because of a material misrepresentation or omission concerning the medical information requested in your application, or deny coverage for death caused by suicide, may have expired or may expire earlier than it will under the proposed policy.</p>
          <p className="agreementCardTextBold">Your existing policy may have options which are not available under the policy being proposed to you or may not come into effect under the proposed policy until a later time during your life. Also, your proposed policy’s cash values and dividends, if any, may grow slower initially because the company will incur the cost of issuing your new policy. On the other hand, the proposed policy may offer advantages which are more important to you.</p>
          <p className="agreementCardTextBold">If you are considering borrowing against your existing policy to pay the premiums on the proposed policy, you should understand that in the event of your death, the amount of any unpaid loan, including unpaid interest, will be deducted from the benefits of your existing policy thereby reducing your total insurance coverage.</p>
          <p className="agreementCardTextBold">AFTER WE HAVE ISSUED YOUR POLICY, YOU WILL HAVE THIRTY DAYS FROM THE DATE THE NEW POLICY IS RECEIVED BY YOU TO NOTIFY US YOU ARE CANCELING THE POLICY ISSUED ON YOUR APPLICATION AND YOU WILL RECEIVE BACK ALL PAYMENTS YOU MADE TO US.</p>
          <p className="agreementCardTextBold">You are urged not to take action to terminate or alter your existing life insurance coverage until you have been issued the new policy, examined it and have found it acceptable to you.</p>
        </div>
      );
      case MASSACHUSETTS: return (
        <div>
          <p className="agreementCardTextBold">You are contemplating the purchase of a life insurance policy or annuity contract. In some cases this purchase may involve discontinuing or changing an existing policy or contract. If so, a replacement is occurring. Financed purchases are also considered replacements.</p>
          <p className="agreementCardTextBold">This notice is required by the Commissioner of Insurance because you have indicated that you are buying a new life insurance policy or annuity and discontinuing or changing an existing one.  Such a decision could be a good one, or a mistake. You will not know for sure until you make a careful comparison of your existing policy and the proposed replacement policy. Premiums alone are not determinative of low cost. Take the time to obtain and understand the facts.</p>
          <p className="agreementCardTextBold">We are required by law to notify your existing company that you may be replacing their policy.  Consider both sides before you decide. This way you can be sure you are making a decision that is in your best interest.</p>
          <p className="agreementCardTextBold"><u>Cash Value Insurance</u></p>
          <p className="agreementCardTextBold">To make a comparison of cash value policies (policies with loan or surrender values in addition to death protection), consideration must be given to each policy's cash values, premiums, coverage amounts and dividends, if any, over the life of the policy.</p>
          <p className="agreementCardTextBold">To simplify this task, you may wish to request from your existing insurance company and the company issuing the replacement policy yield index figures for five, ten and 20 years. The yield index is a percentage that represents an estimate of the interest rate the insurer projects you will earn on the savings portion of the cash value policy. The policy with the higher yield index will generally be the better buy.</p>
          <p className="agreementCardTextBold">The Yield Index Committee of the National Association of Insurance Commissioners in 1986 devised a method for calculating a yield index. In order to request this yield index information, merely check the box below and your request will be forwarded to both insurance companies.  You can also compare the cash values and/or surrender values listed in the replacing company's policy summary for the first five policy years with those in your current policy for the next five years. Low cash values or surrender values in early policy years are often the result of high expenses associated with issuing a new policy. If the replacement policy has low values in its early years, it will usually take longer for it to provide you with benefits that equal or exceed the benefits of your existing policy. In some cases, the replacement policy may never provide benefits equal to those in your present policy.</p>
          <p className="agreementCardTextBold"><u>Term Insurance</u></p>
          <p className="agreementCardTextBold"> If you are replacing your present insurance policy with term insurance (policies that provide death protection only), it makes sense to shop for a low cost policy. Costs for term insurance vary widely and substantial savings may be realized by comparison shopping. Premiums alone are not always determinative of low cost since some policies pay dividends and others do not. You may wish to request interest-adjusted cost indices for five, ten and 20 years from several insurance companies including your existing insurer to help you compare term insurance premiums. The policy with the lower index numbers is usually the better buy.</p>
        </div>
      );
      case MICHIGAN: return (
        <div>
          <p className="agreementCardTextBold">THIS NOTICE IS FOR YOUR PROTECTION AND IS REQUIRED BY REGULATIONS OF THE MICHIGAN COMMISSIONER OF INSURANCE. PLEASE READ IT CAREFULLY.</p>
          <p className="agreementCardTextBold">Dropping or changing your existing life insurance to replace it with a new life insurance policy may be disadvantageous because:</p>
          <ul className="agreementCardTextBold">
            <li>A company can deny a claim during the first two years if it can be shown that you withheld information from your application which was important to the decision of whether to insure you. This is called the “CONTESTABLE PERIOD”. If you drop or change policies, you may have to go through the two year period again. </li>
            <li>You may pay HIGHER RATES for identical coverage because of your age. Life insurance rates go up as you get older.</li>
          </ul>
          <p className="agreementCardTextBold">BEFORE YOU DROP, CHANGE OR CASH IN YOUR PRESENT INSURANCE and apply for new insurance, you should:</p>
          <ol className="agreementCardTextBold">
            <li>Compare the policy BENEFITS and OPTIONS. The agent is required by law to provide you with all pertinent facts of  the change and the insurance company you are considering must notify the company that issued your existing policy.</li>
            <li>Be aware that you may be required to provide EVIDENCE OF INSURABILITY. If your health condition has changed since the application was taken on your present policy, you may be required to pay additional premiums under the new  policy, or be denied coverage.</li>
            <li>Compare the loan interest rate. The interest rate for new policies is probably higher than for the existing policy. Therefore, you will pay more when you want to borrow the cash value. If you are considering borrowing against your  existing policy to pay the premiums on the proposed policy, you should understand that in the event of your death, the  amount of any unpaid loan, including unpaid interest, will be deducted from the benefits of your existing policy.</li>
            <li>Find out if the existing policy and/or the proposed policy offers DIVIDENDS OR EXCESS INTEREST. Dividends or excess interest can have a significant impact on net policy cost. Remember that no company can guarantee the  amount of dividends it will pay in the future, nor can excess interest projections be presented as to imply a guarantee.</li>
            <li>CONTACT THE AGENT OF YOUR PRESENT COMPANY. Your present company can often make changes in your existing insurance on terms which are more favorable to you than can another company.</li>
            <li>Find out if there are income or estate tax consequences if you drop or change your present policy.</li>
          </ol>
          <p className="agreementCardTextBold">You should not drop or change your existing life insurance coverage until after you have been issued the new policy, examined it and found it to be acceptable to you.</p>
          <p className="agreementCardTextBold">REMEMBER YOU HAVE THIRTY (30) DAYS AFTER RECEIPT OF THE POLICY TO CANCEL AND OBTAIN A FULL REFUND.</p>
        </div>
      );
      case NEVADA: return (
        <div>
          <p className="agreementCardTextBold">IMPORTANT NOTICE REGARDING THE REPLACEMENT OF YOUR POLICY OF LIFE INSURANCE</p>
          <p className="agreementCardTextBold">You have been offered a policy to replace all or part of your existing policy of life insurance.</p>
          <p className="agreementCardTextBold">Before you replace your existing policy you should consider whether you could suffer a FINANCIAL LOSS under the new policy because of your AGE or the condition of your HEALTH. You should also consider whether you will pay more for premiums because of your age or health.</p>
          <p className="agreementCardTextBold">You WILL incur additional costs to acquire the new policy, including the payment of commissions to the agent advocating the replacement of your existing policy.</p>
          <p className="agreementCardTextBold">To make an informed decision about the replacement of your policy, you should discuss the provisions of your existing policy with your agent or the company which issued it to determine whether your policy can be changed to meet your present needs.</p>
          <p className="agreementCardTextBold">Your new policy provides 30 days for you to decide whether you wish to keep it.</p>
          <p className="agreementCardTextBold">The agent who is offering to replace your existing policy is required to obtain your signature on this notice.  Also, the agent will be notifying your existing company that you are considering the replacement of your policy.</p>
        </div>
      );
      case OKLAHOMA: return (
        <div>
          <p className="agreementCardTextBold">This Notice is for Your Benefit and is Required by Law</p>
          <ol className="agreementCardTextBold">
            <li>If you are urged to purchase life insurance and to surrender, lapse, or in any other way change the status of existing life insurance, the agent is required to give you this notice.</li>
            <li>It may not be advantageous to drop or change existing life insurance in favor of new life insurance, whether issued by the same or a different insurance company. Some of the disadvantages are:
              <ol type="a">
                <li>The amount of the annual premium under an existing policy may be lower than that under a new policy having the same or similar benefits.</li>
                <li>Generally, the initial costs of life insurance policies are charged against the cash value increases in the earlier policy years, the replacement of an old policy could result in the policyholder sustaining the burden of these costs twice.</li>
                <li>The incontestable and suicide clauses begin anew in a new policy. This could result in a claim under a new policy being denied by the company which would have been paid under the old policy.</li>
                <li>Existing policies may have more favorable provisions than new policies in such areas as settlement options and disability benefits.</li>
                <li>An existing policy may have a reserve value in addition to any cash value which may be of some benefit to the insured.</li>
                <li>The insurance company carrying your current insurance policy can often make a desired change on terms which would be more favorable than if existing insurance is replaced with new insurance.</li>
              </ol>
            </li>
            <li>It may not be advantageous to change an existing policy to reduce paid—up or extended term insurance or to borrow  against its loan value beyond your expected ability or intention to repay in order to obtain funds for premiums on a new  policy.</li>
            <li>There may be a situation in which a replacement policy is advantageous. You may want to receive the comments of the  present insurance company before deciding this important financial matter.</li>
          </ol>
          <p className="agreementCardTextBold">I hereby acknowledge that I received the above “Notice to Applicants Regarding Replacement of Life Insurance or an Annuity” before I signed the application for the proposed new insurance.</p>
        </div>
      );
      case PENNSYLVANIA: return (
        <div>
          <p className="agreementCardTextBold">You have indicated that you intend to replace existing life insurance or annuity coverage in connection with the purchase of our life insurance or annuity policy. So please read below points carefully.</p>
          <p className="agreementCardTextBold">Whether it is to your advantage to replace your existing insurance or annuity coverage, only you can decide. It is in your best interest, however, to have adequate information before a decision to replace your present coverage becomes final so that you may understand the essential features of the proposed policy and your existing insurance or annuity coverage.</p>
          <p className="agreementCardTextBold">You may want to contact your existing life insurance or annuity company or its agent for additional information and advice or discuss your purchase with other advisors. Your existing company will provide this information to you. The information you receive should be of value to you in reaching a final decision.</p>
          <p className="agreementCardTextBold">If either the proposed coverage or the existing coverage you intend to replace is participating, you should be aware that dividends may materially reduce the cost of insurance and are an important factor to consider. Dividends, however, are not guaranteed.</p>
          <p className="agreementCardTextBold">You should recognize that a policy which has been in existence for a period of time may have certain advantages to you over a new policy. If the policy coverages are basically similar, the premiums for a new policy may be higher because rates increase as your age increases.  Under your existing policy, the period of time during which the issuing company could contest the policy because of a material misrepresentation or omission concerning the medical information requested on your application, or deny coverage for death caused by suicide, may have expired or may expire earlier than it will under the proposed policy. Your existing policy may have options which are not available under the policy being proposed to you or may not come into effect under the proposed policy until a later time during your life. Also, your proposed policy’s cash values and dividends, if any, may grow slower initially because the company will incur the cost of issuing your new policy. On the other hand, the proposed policy may offer advantages which are more important to you.</p>
          <p className="agreementCardTextBold">If you are considering borrowing against your existing policy to pay the premiums on the proposed policy, you should understand that in the event of your death, the amount of any unpaid loan, including unpaid interest, will be deducted from the benefits of your existing policy thereby reducing your total insurance coverage.</p>
          <p className="agreementCardTextBold">After we have issued your policy, you will have 20 days from the date the new policy is received by you to notify us you are canceling the policy issued on your application and you will receive back all payments made to us.</p>
          <p className="agreementCardTextBold">You are urged not to take action to terminate your existing life insurance or annuity coverage until after you have been issued the new policy, examined it and have found it acceptable to you.</p>
        </div>
      );
      case WASHINGTON: return (
        <div>
          <p className="agreementCardTextBold">The decision to buy a new life insurance policy or annuity and discontinue or change an existing one is very important. Your decision could be a good one - or a mistake. It should be carefully considered. The Washington State Insurance Commissioner requires us to give you this notice to help you make a wise decision.</p>
          <p className="agreementCardTextBold"><u>Caution:</u> The insurance commissioner suggests you consider these points:</p>
          <p className="agreementCardTextBold">Usually, contestable and suicide periods start again under a new policy. Benefits might be excluded under a new policy that would be paid under existing insurance.</p>
          <p className="agreementCardTextBold">Terminating or altering existing coverage, before new insurance has been issued, might leave you unable to purchase other life insurance or let you buy it only at substantially higher rates.</p>
          <p className="agreementCardTextBold">You are entitled to advice from the existing agent or company, such advice might be helpful.</p>
        </div>
      );
      case WYOMING: return (
        <div>
          <p className="agreementCardTextBold">Whether it is to your advantage to replace or change your existing insurance or annuity program, only you can decide. It is in your best interest to obtain adequate information in order to compare relatively short and long range costs and benefits before a final decision is made.</p>
          <p className="agreementCardTextBold">The Producer or insurance company assisting you with this new purchase must notify your existing producer or company so that they may prepare a detailed, current statement concerning your existing program for your comparison. Statements and illustrations should not, however, be used as the sole basis to compare policies or contracts. We want you to understand the effects of replacements before you make your purchase decision, and ask that your review the statements listed under “Items to Consider.”</p>
        </div>
      );
      default: return (
        <div>
          <p className="agreementCardTextBold">You are contemplating the purchase of a life insurance policy or annuity contract. In some cases this purchase may involve discontinuing or changing an existing policy or contract. If so, a replacement is occurring.  Financed purchases are also considered replacements.</p>
          <p className="agreementCardTextBold">A replacement occurs when a new policy or contract is purchased and, in connection with the sale, you discontinue making premium payments on an existing policy or contract, or an existing policy or contract is surrendered, forfeited, assigned to the replacing insurer, or otherwise terminated or used in a financed purchase.</p>
          <p className="agreementCardTextBold">A financed purchase occurs when the purchase of a new life insurance policy involves the use of funds obtained by the withdrawal or surrender of or by borrowing some or all of the policy values, including accumulated dividends, of an existing policy to pay all or part of any premium or payment due on the new policy. A financed purchase is a replacement.</p>
          <p className="agreementCardTextBold">You should carefully consider whether a replacement is in your best interest. You will pay acquisition costs and there may be surrender costs deducted from your policy or contract. You may be able to make changes to your existing policy or contract to meet your insurance needs at less cost. A financed purchase will reduce the value of your existing policy or contract and may reduce the amount paid upon the death of the insured.</p>
        </div>
      );
    }
  }

  const getStateSpecificFraudNotice = () => {
    switch (state) {
      case CALIFORNIA:
      case DELAWARE:
      case NORTH_DAKOTA:
      case SOUTH_DAKOTA:
        return "ANY PERSON WHO KNOWINGLY PRESENTS A FALSE OR FRAUDULENT CLAIM FOR PAYMENT OF A LOSS OR BENEFIT OR KNOWINGLY PRESENTS FALSE INFORMATION IN AN APPLICATION FOR INSURANCE IS GUILTY OF A CRIME AND MAY BE SUBJECT TO FINES AND CONFINEMENT IN PRISON.";
      case FLORIDA: return "ANY PERSON WHO KNOWINGLY AND WITH INTENT TO INJURE, DEFRAUD, OR DECEIVE ANY INSURER FILES A STATEMENT OF CLAIM OR ON AN APPLICATION CONTAINING ANY FALSE, INCOMPLETE, OR MISLEADING INFORMATION IS GUILTY OF A FELONY OF THE THIRD DEGREE.";
      default: return "ANY PERSON WHO KNOWINGLY PRESENTS A FALSE STATEMENT IN AN APPLICATION FOR INSURANCE MAY BE GUILTY OF A CRIMINAL OFFENSE AND SUBJECT TO PENALTIES UNDER STATE LAW.";
    }
  }

  const addNewPolicy = () => {
    const newIndex = props.formState.policies.length;
    register({ name: `policies[${newIndex}].insuredName` });
    register({ name: `policies[${newIndex}].company` });
    register({ name: `policies[${newIndex}].ownerName` });
    register({ name: `policies[${newIndex}].insuranceDate` });
    register({ name: `policies[${newIndex}].faceAmount` });
    register({ name: `policies[${newIndex}].policyNumber` });
    register({ name: `policies[${newIndex}].insurerName` });
    const newForm = PolicyPurposeConfirmation.DefaultFormState().policies[0];
    props.onFieldChange('policies', [...props.formState.policies, newForm]);
  }

  const formPolicies = props.formState.policies.map((policyFormState, index) =>
    <PolicyInformation
      formState={policyFormState}
      onRemove={() => {
        props.onFieldChange(
          'policies',
          props.formState.policies.filter((form, formIndex) => formIndex !== index)
        );
        reset();
      }}
      onChange={(newForm) => {
        props.onFieldChange(
          'policies',
          props.formState.policies.map((form, formIndex) => {
            return formIndex === index ? newForm : form;
          })
        );
      }}
      setValue={setValue}
      trigger={triggerValidation}
      key={index}
      index={index}
      register={register}
      showPolicyRemove={props.formState.policies.length > 1}
      errors={errors.policies && errors.policies[index]}
      useExistingPolicyFunds={props.formState.useExistingPolicyFunds}
      state={state}
    />
  )

  return (
    <Container>
      <div className="formPage">
        <Row className="justify-content-center">
          <SvgIcon component={Step7Icon} className="headerIcon" />
        </Row>
        <h4 className="headerTitle">Confirming Your Existing Coverage</h4>
        <h5 className="headerSubtitle">Legal questions we're required to ask.</h5>

        <form className="quote-form" onSubmit={handleSubmit(onSubmit)}>
          <Row className="justify-content-center">
            <Col lg={5}>
              <Form.Group>
                <StyledRadioInput
                  name="pendingApplications"
                  id="pendingApplications"
                  label="Does any Proposed Insured have life insurance or annuity applications pending with other companies?"
                  value={props.formState.pendingApplications}
                  options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                  onChange={e => handleValueChange('pendingApplications', e)}
                  errors={errors.pendingApplications}
                />
              </Form.Group>
              <Form.Group>
                <StyledRadioInput
                  name="existingCoverage"
                  id="existingCoverage"
                  label="Is there any existing life insurance or annuity insurance coverage on the life of any Proposed Insured?"
                  value={props.formState.existingCoverage}
                  options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                  onChange={e => handleValueChange('existingCoverage', e)}
                  errors={errors.existingCoverage}
                />
              </Form.Group>
              {props.formState.existingCoverage === "true" && (
                <div>
                  <Form.Group>
                    <TextInput
                      name="totalInsuredAmountExternal"
                      id="totalInsuredAmountExternal"
                      label="Total Death Benefit"
                      value={props.formState.totalInsuredAmountExternal}
                      onChange={e => props.onFieldChange('totalInsuredAmountExternal', e.target.value)}
                      InputProps={{
                        inputComponent: NumberFormatDollars,
                      }}
                      inputRef={register}
                      errors={errors.totalInsuredAmountExternal}
                    />
                  </Form.Group>
                  <Form.Group>
                    <StyledRadioInput
                      name="replaceExistingCoverage"
                      id="replaceExistingCoverage"
                      label="Will the insurance applied for replace or otherwise reduce in value any existing life insurance or annuity in force?"
                      value={props.formState.replaceExistingCoverage}
                      options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                      onChange={e => handleValueChange('replaceExistingCoverage', e)}
                      errors={errors.replaceExistingCoverage}
                    />
                  </Form.Group>
                  {props.formState.replaceExistingCoverage === "true" && (
                    <div className="mb-3">
                      {
                        showValidationMessage && (
                          <div className="text-danger mb-1">Replacement death benefit amount should not be greater than the total death benefit indicated above.</div>)
                      }
                      {formPolicies}
                      {props.formState.policies && props.formState.policies.length < 6 ? (
                        <Button color="primary" onClick={() => addNewPolicy()} className="d-flex m-auto">
                          ADD ANOTHER POLICY
                        </Button>) :
                        (
                          <div className="text-danger text-center">No additional policy may be added in the eApplication.</div>
                        )}
                    </div>
                  )}
                  {state === FLORIDA && (
                    <Form.Group>
                      <StyledRadioInput
                        name="receiveComparativeInfoForm"
                        id="receiveComparativeInfoForm"
                        label="Florida regulations give you the right to receive a written Comparative Info Form which summarizes your policy values. Indicate whether or not you wish a Comparative Information Form from the proposed company and your existing insurer or insurers."
                        value={props.formState.receiveComparativeInfoForm}
                        options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                        onChange={e => handleValueChange('receiveComparativeInfoForm', e)}
                        errors={errors.receiveComparativeInfoForm}
                      />
                    </Form.Group>
                  )}

                  {props.formState.replaceExistingCoverage === "true" && (
                    <div>
                      {state === MASSACHUSETTS && (
                        <Form.Group>
                          <CheckboxInput
                            name="yieldIndicesForCashValue"
                            id="yieldIndicesForCashValue"
                            checked={ConvertStringToBoolean(props.formState.yieldIndicesForCashValue)}
                            onChange={e => handleValueChange('yieldIndicesForCashValue', ConvertBooleanToString(e.target.checked))}
                            errors={errors.yieldIndicesForCashValue}
                            inputRef={register}
                            label="Check box to request yield indices for cash value policies"
                          />
                        </Form.Group>
                      )}
                      {!excludeReplacingReasonStates.includes(state) && (
                        <div>
                          <Form.Group>
                            <TextInput
                              name="reasonOfReplacement"
                              id="reasonOfReplacement"
                              label="Reason for Replacement"
                              value={props.formState.reasonOfReplacement}
                              onChange={e => props.onFieldChange('reasonOfReplacement', e.target.value)}
                              errors={errors.reasonOfReplacement}
                              inputRef={register}
                              multiline={true}
                              rows="5"
                              InputLabelProps={{ className: classes.multiLine }}
                            />
                          </Form.Group>
                        </div>
                      )}

                      <Row className="justify-content-center">
                        <Col lg={12}>
                          {(state === WYOMING) &&
                            <div>
                              <Card className="agreementCard">
                                <CardContent className="scrollbar">
                                  <div className="overflow">
                                    <p className="agreementCardTextBold"><u>ITEMS TO CONSIDER</u></p>
                                    <ol className="agreementCardTextBold">
                                      <li>Due to a possible change insurability status (health, occupation, or high risk recreational activities) you might be denied new coverage or the premium may be higher than a standard premium.</li>
                                      <li>The Incontestability and Suicide Clause time periods would probably begin anew in a new policy. This could possibly result in a claim  being denied that might otherwise have been paid under an existing policy or contract.</li>
                                      <li>Your present insurance company may be able to modify your existing plan on terms which may be more favorable for you than  completely replacing it with a new policy or contract.</li>
                                      <li>Don’t terminate or alter your existing policy until after the new policy has been delivered to you and accepted by you.</li>
                                      <li>If you terminate your existing policy, you may incur surrender charges and/or penalties. The new policy you are purchasing may have a  new surrender charge period and/or sales charges.</li>
                                      <li>There may be tax consequences in replacing an existing policy. Is there a benefit from favorable “grandfathered” treatment of the old  policy under federal tax code? Is this transaction a “tax-free” exchange? See your tax advisor for specific tax advice regarding the  proposed transaction.</li>
                                      <li>Take your time in making your decision about purchasing the new policy. Gather all information about the new policy and compare it to  your old policy. Remember, you do not have to make a decision during the first meeting with your sales person.</li>
                                      <li> REMEMBER: Following receipt of a new life insurance policy or annuity contract you should immediately examine its contents. If you are  not satisfied with it for any reason, you have the right to return it within the thirty (30) day “examination period” to the insurer at its home  office or branch office or to the producer through whom it was purchased, for a full refund of premium. If you do return the policy or contract, you should request a dated receipt indicating that it was Returned.</li>
                                    </ol>
                                  </div>
                                </CardContent>
                              </Card>
                              <Form.Group>
                                <CheckboxInput
                                  name="ownerItemsToConsider"
                                  id="ownerItemsToConsider"
                                  checked={ConvertStringToBoolean(props.formState.ownerItemsToConsider)}
                                  onChange={e => props.onFieldChange('ownerItemsToConsider', ConvertBooleanToString(e.target.checked))}
                                  errors={errors.ownerItemsToConsider}
                                  inputRef={register}
                                  label={`I read the "Items to Consider"`}
                                  className={classes.setMargin}
                                  labelClassName={classes.setCheckBox}
                                />
                              </Form.Group>
                            </div>
                          }
                        </Col>
                      </Row>

                      {stateList.includes(state) && (
                        <div>
                          <Form.Group>
                            <StyledRadioInput
                              name="terminateExistingPolicy"
                              id="terminateExistingPolicy"
                              label="Are you considering discontinuing making premium payments, surrendering, forfeiting, assigning to the insurer, or otherwise terminating your existing policy or contract?"
                              value={props.formState.terminateExistingPolicy}
                              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                              onChange={e => handleValueChange('terminateExistingPolicy', e)}
                              errors={errors.terminateExistingPolicy}
                            />
                          </Form.Group>
                          <Form.Group>
                            <StyledRadioInput
                              name="useExistingPolicyFunds"
                              id="useExistingPolicyFunds"
                              label="Are you considering using funds from your existing policies or contracts to pay premiums due on the new policy or contract?"
                              value={props.formState.useExistingPolicyFunds}
                              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                              onChange={e => handleValueChange('useExistingPolicyFunds', e)}
                              errors={errors.useExistingPolicyFunds}
                            />
                          </Form.Group>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <Form.Group>
                <FormControl component="fieldset">
                  <FormLabel component="legend" className={classes.labelFont}>Under penalties of perjury, I as the Owner certify that (check all that apply):</FormLabel>
                  <Form.Group>
                    <CheckboxInput
                      name="usCitizen"
                      id="usCitizen"
                      checked={ConvertStringToBoolean(props.formState.usCitizen)}
                      onChange={e => props.onFieldChange('usCitizen', ConvertBooleanToString(e.target.checked))}
                      errors={errors.usCitizen}
                      inputRef={register}
                      label="I am a U.S. citizen or other U.S. person, and the number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me), and,"
                      className={classes.setMargin}
                      labelClassName={classes.setCheckBox}
                    />
                  </Form.Group>
                  <Form.Group>
                    <CheckboxInput
                      name="backupWitholding"
                      id="backupWitholding"
                      checked={ConvertStringToBoolean(props.formState.backupWitholding)}
                      onChange={e => props.onFieldChange('backupWitholding', ConvertBooleanToString(e.target.checked))}
                      errors={errors.backupWitholding}
                      inputRef={register}
                      label="I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding."
                      className={classes.setMargin}
                      labelClassName={classes.setCheckBox}
                    />
                  </Form.Group>
                </FormControl>
              </Form.Group>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg={7}>
              <Card className="agreementCard">
                <CardContent className="scrollbar">
                  <div className="overflow">
                    {state === CALIFORNIA && (
                      <p className="agreementCardTextBold">
                        IN ACCORDANCE WITH CALIFORNIA STATE LAW, WE MUST PROVIDE YOU WITH THE FOLLOWING SALES NOTICE: This sale or liquidation of any stock, bond, IRA, certificate of deposit, mutual fund, annuity, or other asset to fund the purchase of this product may have tax consequences, early withdrawal penalties, or other costs or penalties.  You or Your agent may wish to consult with independent legal or financial advice before selling or liquidating any assets prior to the purchase of any life or annuity products being solicited, offered for sale or sold.
                      </p>
                    )}
                    <p className="agreementCardTextBold">
                      By providing Your Authorization and Acknowledgment, You:
                    </p>
                    <p className="agreementCardTextBold">
                      • AGREE any policy issued on this application will be deemed to be delivered in and governed by the laws of the jurisdiction where the Owner resides at the time of the application, as evidence by the address provided in this application.
                    </p>
                    <p className="agreementCardTextBold">
                      • ACKNOWLEDGE that the USA PATRIOT ACT requires all financial institutions, including insurance companies, to verify the identity of their customers. Providing your name, address, date of birth and taxpayer identification number allows Americo to verify your identity. Americo’s verification process may include the use of third-party sources to verify the information you provide.
                    </p>
                    <p className="agreementCardTextBold">
                      • You furthermore Agree to the following:
                    </p>
                    <p className="agreementCardTextBold">
                      • THE ANSWERS AND STATEMENTS IN THE APPLICATION FOR INSURANCE ARE THE BASIS FOR ANY POLICY ISSUED BY AMERICO AND NO INFORMATION WILL BE CONSIDERED TO HAVE BEEN GIVEN TO AMERICO UNLESS IT IS STATED IN THE APPLICATION.
                    </p>
                    <p className="agreementCardTextBold">
                      • YOUR SALES REPRESENTATIVE DOES NOT HAVE AMERICO’S AUTHORIZATION TO WAIVE THE ANSWER TO ANY QUESTION IN THIS APPLICATION, NOR DECIDE ON THE INSURABILITY, NOR WAIVE ANY OF THE COMPANY’S UNDERWRITING REQUIREMENTS, NOR CHANGE ANY CONTRACT.
                    </p>
                    {state === CALIFORNIA ? (
                      <p className="agreementCardTextBold">
                        • ALL ANSWERS AND STATEMENTS IN THIS APPLICATION FOR INSURANCE, AS THEY PERTAIN TO YOU, ARE TRUE AND COMPLETE TO THE BEST OF YOUR KNOWLEDGE AND BELIEF.
                      </p>
                    ) : (
                      <p className="agreementCardTextBold">
                        • ALL ANSWERS AND STATEMENTS IN THIS APPLICATION FOR INSURANCE, AS THEY PERTAIN TO YOU, ARE TRUE AND COMPLETE TO THE BEST OF YOUR KNOWLEDGE AND BELIEF. CONSISTENT WITH STATE LAWS, ANY FALSE ANSWER MAY SERVE AS A BASIS FOR A DENIAL OF A CLAIM AND/OR RESCISSION OF THE POLICY.
                      </p>
                    )}
                    <p className="agreementCardTextBold text-center">
                      <span>IMPORTANT FRAUD NOTICE:</span><br />
                      {getStateSpecificFraudNotice()}
                    </p>
                    {props.formState.replaceExistingCoverage === "true" && (
                      <div>
                        <p className="agreementCardHeader"><u>Replacement of Life Insurance and Annuities</u></p>
                        {getStateSpecificVerbiage()}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg={5}>
              <BackNext onBack={props.onBack} disabled={showFailureMessage} loading={isLoading} />
            </Col>
          </Row>
          {showFailureMessage && (
            <Row className="justify-content-center">
              <Col lg={7}>
                <div className="text-danger text-center">We’re sorry, something has gone wrong, but don’t worry - your request is registered and one of our licensed concierge team members will reach out to you.</div>
              </Col>
            </Row>
          )}
        </form>
      </div>
      {exceedCovgAmtModalShow &&
        <ExceedCovgAmountModal
          history={props.history}
          fullFormState={props.fullFormState}
          onHide={() => setExceedCovgAmtModalShow(false)}
          show={() => setExceedCovgAmtModalShow(true)}
          onMultiFieldChange={props.onMultiFieldChange}
        />
      }
    </Container>
  )
}

function PolicyInformation(props) {

  const onFieldChange = (field, value) => {
    props.onChange({ ...props.formState, [field]: value })
  }

  const handleValueChange = (field, value, longFieldName) => {
    props.setValue(longFieldName, value);
    onFieldChange(field, value);
    props.trigger(longFieldName);
  }

  return (
    <div>
      <Form.Group>
        <TextInput
          name="insuredName"
          id="insuredName"
          label="Insured's Name"
          value={props.formState.insuredName}
          onChange={e => handleValueChange('insuredName', e.target.value, `policies[${props.index}].insuredName`)}
          errors={props.errors && props.errors.insuredName}
          inputRef={props.register}
        />
      </Form.Group>
      <Form.Group>
        <SelectInput
          name="company"
          id="company"
          label="Company"
          value={props.formState.company}
          onChange={e => handleValueChange('company', e.target.value, `policies[${props.index}].company`)}
          errors={props.errors && props.errors.company}
          selectOptions={InsuranceProviders()}
          inputRef={props.register}
        />
      </Form.Group>
      {
        props.formState.company === "Other" &&
        <Form.Group>
          <TextInput
            name="insurerName"
            id="insurerName"
            label="Insurer Name"
            value={props.formState.insurerName}
            onChange={e => handleValueChange('insurerName', e.target.value, `policies[${props.index}].insurerName`)}
            errors={props.errors && props.errors.insurerName}
            inputRef={props.register}
          />
        </Form.Group>
      }
      <Form.Group>
        <TextInput
          name="policyNumber"
          id="policyNumber"
          label="Policy Number (or type 'Unknown')"
          value={props.formState.policyNumber}
          onChange={e => handleValueChange('policyNumber', e.target.value, `policies[${props.index}].policyNumber`)}
          errors={props.errors && props.errors.policyNumber}
          inputRef={props.register}
        />
      </Form.Group>
      <Form.Group>
        <TextInput
          name="ownerName"
          id="ownerName"
          label="Owner's Name"
          value={props.formState.ownerName}
          onChange={e => handleValueChange('ownerName', e.target.value, `policies[${props.index}].ownerName`)}
          errors={props.errors && props.errors.ownerName}
          inputRef={props.register}
        />
      </Form.Group>
      <Form.Group>
        <TextInput
          name="insuranceDate"
          id="insuranceDate"
          label="Policy Effective Date"
          value={props.formState.insuranceDate}
          errors={props.errors && props.errors.insuranceDate}
          inputRef={props.register}
          onChange={(e) => handleValueChange('insuranceDate', e.target.value, `policies[${props.index}].insuranceDate`)}
          InputProps={{
            inputComponent: InputMaskDate,
          }}
        />
      </Form.Group>

      <Form.Group>
        <TextInput
          name="faceAmount"
          id="faceAmount"
          label="Death Benefit"
          value={props.formState.faceAmount}
          onChange={(e) => handleValueChange('faceAmount', e.target.value, `policies[${props.index}].faceAmount`)}
          InputProps={{
            inputComponent: NumberFormatDollars,
          }}
          inputRef={props.register}
          errors={props.errors && props.errors.faceAmount}
        />
      </Form.Group>

      {props.showPolicyRemove &&
        <Form.Group>
          <Button onClick={props.onRemove}>Remove this Policy</Button>
        </Form.Group>
      }
    </div>
  )
}

PolicyPurposeConfirmation.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
}

PolicyInformation.propTypes = {
  formState: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.any,
}

PolicyPurposeConfirmation.ValidationSchema = (birthdate) => {
  return object().shape({
    pendingApplications: string()
      .required('This field is required.')
      .matches(/^false/, 'Based on your answer, the Proposed Insured is not eligible for this product.'),
    existingCoverage: string().required('This field is required.'),
    usCitizen: string().required(),
    backupWitholding: string().required(),
    replaceExistingCoverage: string().when('existingCoverage', {
      is: "true",
      then: string().required('This field is required.'),
      otherwise: string().notRequired()
    }),
    terminateExistingPolicy: string().when(['existingCoverage', 'replaceExistingCoverage', 'state'], {
      is: (existingCoverage, replaceExistingCoverage, state) => existingCoverage === "true" && replaceExistingCoverage === "true" && stateList.includes(state),
      then: string().required('This field is required.'),
      otherwise: string().notRequired()
    }),
    useExistingPolicyFunds: string().when(['existingCoverage', 'replaceExistingCoverage', 'state'], {
      is: (existingCoverage, replaceExistingCoverage, state) => existingCoverage === "true" && replaceExistingCoverage === "true" && stateList.includes(state),
      then: string().when('terminateExistingPolicy', {
        is: "false",
        then: string().required('This field is required.').matches(/^true/, "Financial and Replacement questions are answered no, but replacement was indicated on this policy. Please resolve discrepancy."),
        otherwise: string().required('This field is required.')
      }),
      otherwise: string().notRequired()
    }),
    policies: mixed().when('existingCoverage', {
      is: "true",
      then: mixed().when('replaceExistingCoverage', {
        is: "true",
        then: yuparray(object().shape({
          insuredName: string().required('Insured name is required.'),
          company: string().required('Company is required.')
            .notOneOf(['Americo Financial', 'Great Southern'], 'Coverage is not available when replacing a policy from either Great Southern or Americo Financial.'),
          insurerName: string().when('company', {
            is: 'Other',
            then: string().required('Insurer Name is required.'),
            otherwise: string().notRequired()
          }),
          ownerName: string().required('Owner name is required.'),
          insuranceDate: string()
            .transform(emptyStringToUndefined)
            .typeError('Invalid date.')
            .test('validDate', 'Please enter a valid date.', (value) => isValid(new Date(value)))
            .test('dateTest', 'Please verify Policy Effect Date.', (value) => {
              if(!value){
                return false;
              }
              var year = value.split('/')[2];
              if (parseInt(year, 10) < 1850) {
                return false;
              }

              if (isBefore(new Date(value), new Date(birthdate))) {
                return false;
              }

              if (isAfter(new Date(value), new Date())) {
                return false;
              }

              return true;
            })
            .required('Policy Effective Date is required.'),
          faceAmount: string().required('Death Benefit is required.'),
          replacement: string().notRequired(),
          policyNumber: string().required('Policy Number is required.'),
        })),
        otherwise: mixed().notRequired(),
      }),
      otherwise: mixed().notRequired(),
    }),
    ownerItemsToConsider: mixed().when(['replaceExistingCoverage', 'state'], {
      is: (replaceExistingCoverage, state) => replaceExistingCoverage === "true" && state === WYOMING,
      then: mixed().oneOf([true], 'You must read the Items to Consider.'),
      otherwise: mixed().notRequired(),
    }),

    yieldIndicesForCashValue: string().when(['replaceExistingCoverage', 'state'], {
      is: (replaceExistingCoverage, state) => replaceExistingCoverage === "true" && state === MASSACHUSETTS,
      then: string().required("This field is required."),
      otherwise: string().notRequired(),
    }),
    receiveComparativeInfoForm: string().when(['existingCoverage', 'state'], {
      is: (existingCoverage, state) => existingCoverage === "true" && state === FLORIDA,
      then: string().required("This field is required."),
      otherwise: string().notRequired(),
    }),
    reasonOfReplacement: string().when(['replaceExistingCoverage', 'state', 'existingCoverage'], {
      is: (replaceExistingCoverage, state, existingCoverage) => existingCoverage === "true" && replaceExistingCoverage === "true" && !excludeReplacingReasonStates.includes(state),
      then: string().required('Reason for replacement is required.').max(615, 'Response must be 615 characters or less.'),
      otherwise: string().notRequired()
    }),
    totalInsuredAmountExternal: string().when('existingCoverage', {
      is: "true",
      then: string().required('Total Death Benefit is required.'),
      otherwise: string().notRequired()
    }),
  })
};

PolicyPurposeConfirmation.DefaultFormState = (prefill = false) => {
  return {
    pendingApplications: prefill ? "false" : "",
    existingCoverage: prefill ? "false" : "",
    replaceExistingCoverage: prefill ? "false" : "",
    usCitizen: prefill ? "true" : "false",
    backupWitholding: prefill ? "true" : "false",
    terminateExistingPolicy: prefill ? "false" : "",
    useExistingPolicyFunds: prefill ? "false" : "",
    reasonOfReplacement: prefill ? "Test" : "",
    receiveComparativeInfoForm: prefill ? "false" : "",
    yieldIndicesForCashValue: "false",
    ownerItemsToConsider: prefill ? "true" : "",
    totalInsuredAmountExternal: prefill ? "" : "",
    policies: [
      {
        insuredName: prefill ? "Demo John" : "",
        company: prefill ? "Guardian Life" : "",
        ownerName: prefill ? "Demo Rhea" : "",
        insuranceDate: prefill ? "09/19/2000" : "",
        faceAmount: prefill ? "83702" : "",
        replacement: prefill ? "External" : "",
        policyNumber: prefill ? "123213123" : "",
        insurerName: prefill ? "Other" : ""
      }
    ]
  }
}



// WEBPACK FOOTER //
// ./src/components/Quoters/Americo/steps/PolicyPurposeConfirmation.js